"use client";

import React from "react";
import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Button from "@/components/ui/button/Button";
import Radio from "@/components/form/input/Radio";
import Checkbox from "@/components/form/input/Checkbox";
import Pagination from "@/components/tables/Pagination";
import { Modal } from "@/components/ui/modal/index";
import PurchaseInvoicePrint from "@/components/print/PurchaseInvoicePrint";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

function fmt(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function buildInvoiceItemRows(detail) {
  const challans = Array.isArray(detail?.challans) ? detail.challans : [];
  return challans.flatMap((ch) => {
    const items = Array.isArray(ch?.items) ? ch.items : [];
    return items.map((it, idx) => ({
      key: `${ch?.id || "ch"}-${it?.id || idx}`,
      challan_no: ch?.challan_no || "—",
      material_name: it?.material?.name || "—",
      qty: it?.qty,
      rate: it?.rate,
      amount: it?.amount,
      remark: it?.remark || "",
    }));
  });
}

/** DRF-style list payload; supports alternate `total` keys and `next` when `count` is omitted. */
function readPaginatedListMeta(raw) {
  if (!raw || typeof raw !== "object") {
    return { count: null, hasNext: false, hasPrevious: false };
  }
  const tryCount = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of ["count", "total", "total_count"]) {
      const n = Number(obj[key]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return null;
  };
  const count = tryCount(raw) ?? tryCount(raw.data);
  return {
    count,
    hasNext: Boolean(raw.next),
    hasPrevious: Boolean(raw.previous),
  };
}

const PAGE_SIZE = 20;

export default function RmcPurchaseInvoices() {
  const qc = useQueryClient();
  const isoToday = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [filters, setFilters] = React.useState({
    date_from: isoToday,
    date_to: isoToday,
    party_id: "",
    tax_type: "",
  });
  const [page, setPage] = React.useState(1);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [detailId, setDetailId] = React.useState(null);
  const [createPartyId, setCreatePartyId] = React.useState("");
  const [selectedChallanIds, setSelectedChallanIds] = React.useState([]);
  const [taxType, setTaxType] = React.useState("GST");
  const [invoiceDeleteId, setInvoiceDeleteId] = React.useState(null);
  const printRef = React.useRef(null);

  const { data: parties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties()).data,
  });

  const queryParams = React.useMemo(
    () => ({
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      ...(filters.party_id ? { party_id: filters.party_id } : {}),
      ...(filters.tax_type ? { tax_type: filters.tax_type } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [filters, page]
  );

  const { data: invoicesData, isFetching } = useQuery({
    queryKey: ["purchaseInvoices", queryParams],
    queryFn: async () => (await rmcClient.listPurchaseInvoices(queryParams)).data,
    keepPreviousData: true,
  });

  const rows = React.useMemo(() => {
    if (Array.isArray(invoicesData)) return invoicesData;
    if (Array.isArray(invoicesData?.results)) return invoicesData.results;
    if (Array.isArray(invoicesData?.data?.results)) return invoicesData.data.results;
    return [];
  }, [invoicesData]);

  const paginationMeta = React.useMemo(() => readPaginatedListMeta(invoicesData), [invoicesData]);

  const totalPages = React.useMemo(() => {
    if (paginationMeta.count != null) {
      return Math.max(1, Math.ceil(paginationMeta.count / PAGE_SIZE));
    }
    if (paginationMeta.hasNext) {
      return Math.max(page + 1, 1);
    }
    return Math.max(1, page);
  }, [paginationMeta, page]);

  React.useEffect(() => {
    setPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const currentPage = Math.min(page, totalPages);

  const { data: challansData, isFetching: isFetchingChallans } = useQuery({
    queryKey: ["purchaseChallans", "invoiceCreate", createPartyId || "all"],
    queryFn: async () =>
      (
        await rmcClient.listPurchaseChallans({
          ...(createPartyId ? { party_id: createPartyId } : {}),
          pagination: false,
        })
      ).data,
    enabled: createOpen,
  });

  const challans = React.useMemo(() => {
    if (Array.isArray(challansData)) return challansData;
    if (Array.isArray(challansData?.results)) return challansData.results;
    if (Array.isArray(challansData?.data?.results)) return challansData.data.results;
    return [];
  }, [challansData]);

  const { data: detail, isLoading: isDetailLoading } = useQuery({
    queryKey: ["purchaseInvoice", detailId],
    queryFn: async () => (await rmcClient.getPurchaseInvoice(detailId)).data,
    enabled: detailId != null,
  });
  const detailItemRows = React.useMemo(() => buildInvoiceItemRows(detail), [detail]);

  const invoicedSet = React.useMemo(() => {
    const set = new Set();
    rows.forEach((inv) => {
      (inv?.challans || []).forEach((ch) => {
        const id = Number(ch?.id);
        if (Number.isFinite(id) && id > 0) set.add(id);
      });
    });
    return set;
  }, [rows]);

  const selectableChallans = React.useMemo(
    () =>
      challans.map((c) => ({
        ...c,
        challanId: Number(c.id),
        alreadyInvoiced: invoicedSet.has(Number(c.id)),
      })),
    [challans, invoicedSet]
  );

  const deleteInvoice = useMutation({
    mutationFn: (id) => rmcClient.deletePurchaseInvoice(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["purchaseInvoices"] });
      qc.invalidateQueries({ queryKey: ["purchaseChallans"] });
      qc.removeQueries({ queryKey: ["purchaseInvoice", id] });
      setInvoiceDeleteId(null);
      setDetailId((prev) => (prev === id ? null : prev));
      toast.success("Purchase invoice deleted");
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const createInvoice = useMutation({
    mutationFn: () => {
      if (!selectedChallanIds.length) throw new Error("missing");
      return rmcClient.createPurchaseInvoice({
        challan_ids: selectedChallanIds,
        tax_type: taxType,
      });
    },
    onSuccess: () => {
      toast.success("Purchase invoice created");
      setCreateOpen(false);
      setSelectedChallanIds([]);
      setCreatePartyId("");
      qc.invalidateQueries({ queryKey: ["purchaseInvoices"] });
      qc.invalidateQueries({ queryKey: ["purchaseChallans"] });
    },
    onError: (e) => {
      const msg = e?.response?.data?.errors?.challan_ids?.[0] || "Failed to create purchase invoice.";
      toast.error(msg);
    },
  });

  const partyName = React.useCallback(
    (id) => parties.find((p) => Number(p.id) === Number(id))?.name || "—",
    [parties]
  );

  const invoicePendingDelete = React.useMemo(() => {
    return rows.find((r) => r.id === invoiceDeleteId) || null;
  }, [rows, invoiceDeleteId]);

  const updateFilters = (next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageBreadCrumb pageTitle="Purchase Invoices" />
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          Create Purchase Invoice
        </Button>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 dark:border-gray-800 dark:bg-gray-900/50">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date From</label>
            <DatePicker
              id="purchase-invoice-filter-from"
              defaultDate={filters.date_from}
              placeholder="From date"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date To</label>
            <DatePicker
              id="purchase-invoice-filter-to"
              defaultDate={filters.date_to}
              placeholder="To date"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
          <SearchableSelect
            label="Party"
            value={filters.party_id}
            onChange={(v) => updateFilters({ party_id: v || "" })}
            options={[{ id: "", name: "All parties" }, ...parties]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
          />
          <SearchableSelect
            label="Tax Type"
            value={filters.tax_type}
            onChange={(v) => updateFilters({ tax_type: v || "" })}
            options={[
              { id: "", name: "All" },
              { id: "GST", name: "GST" },
              { id: "NON_GST", name: "Non-GST" },
            ]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
          />
        </div>
      </div>

      <DataTable
        columns={[
          { key: "invoice_no", header: "Invoice No", render: (r) => r.invoice_no || `PINV-${r.id}` },
          { key: "date", header: "Date", render: (r) => r.date || "—" },
          {
            key: "party",
            header: "Party",
            render: (r) => r.purchase_party?.name || partyName(r.purchase_party_id),
          },
          {
            key: "challans",
            header: "Challans",
            render: (r) => (r.challans || []).map((ch) => ch.challan_no).filter(Boolean).join(", ") || "—",
          },
          { key: "tax", header: "Tax", render: (r) => r.tax_type || "—" },
          { key: "sub_total", header: "Sub Total", render: (r) => `₹${fmt(r.sub_total, 2)}` },
          { key: "total", header: "Total", render: (r) => `₹${fmt(r.total_amount, 2)}` },
        ]}
        data={rows}
        isLoading={isFetching}
        onRowClick={(r) => setDetailId(r.id)}
      />

      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>

      <DrawerPanel
        open={createOpen}
        onClose={() => {
          if (!createInvoice.isPending) {
            setCreateOpen(false);
            setSelectedChallanIds([]);
            setCreatePartyId("");
          }
        }}
        title="Create Purchase Invoice"
        widthClass="max-w-3xl"
      >
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <SearchableSelect
              label="Party"
              value={createPartyId}
              onChange={(v) => {
                setCreatePartyId(v || "");
                setSelectedChallanIds([]);
              }}
              options={parties}
              getSubLabel={(p) => p.gst_number}
              placeholder="All parties"
            />
            <div>
              <p className="mb-2 text-xs text-gray-400">Tax Type</p>
              <div className="flex items-center gap-6">
                <Radio
                  id="create-purchase-invoice-gst"
                  name="create-purchase-invoice-tax"
                  value="GST"
                  label="GST"
                  checked={taxType === "GST"}
                  onChange={() => setTaxType("GST")}
                />
                <Radio
                  id="create-purchase-invoice-non-gst"
                  name="create-purchase-invoice-tax"
                  value="NON_GST"
                  label="Non-GST"
                  checked={taxType === "NON_GST"}
                  onChange={() => setTaxType("NON_GST")}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-800">
            <div className="max-h-[420px] overflow-auto divide-y divide-gray-800">
              {isFetchingChallans ? (
                <p className="p-3 text-xs text-gray-500">Loading challans…</p>
              ) : selectableChallans.length === 0 ? (
                <p className="p-3 text-xs text-gray-500">No challans found for selection.</p>
              ) : (
                selectableChallans.map((c) => {
                  const checked = selectedChallanIds.includes(c.challanId);
                  const label = `${c.challan_no || `PUR-${c.challanId}`} • ${partyName(c.party_id)} • ${c.date || "—"} • ₹${fmt(c.total_amount ?? c.sub_total ?? c.amount, 2)}`;
                  return (
                    <div key={c.challanId} className="flex items-center justify-between gap-3 px-3 py-2">
                      <Checkbox
                        id={`purchase-challan-${c.challanId}`}
                        checked={checked}
                        disabled={c.alreadyInvoiced}
                        onChange={(v) =>
                          setSelectedChallanIds((prev) =>
                            v ? Array.from(new Set([...prev, c.challanId])) : prev.filter((id) => id !== c.challanId)
                          )
                        }
                        label={label}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-gray-800 pt-4">
            <Button size="sm" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={!selectedChallanIds.length} onClick={() => createInvoice.mutate()}>
              {createInvoice.isPending ? "Creating…" : "Create Invoice"}
            </Button>
          </div>
        </div>
      </DrawerPanel>

      <Modal
        isOpen={detailId != null}
        onClose={() => setDetailId(null)}
        className="max-w-5xl w-full max-h-[min(90vh,820px)] overflow-y-auto"
      >
        {isDetailLoading ? (
          <div className="p-4 text-sm text-gray-400">Loading invoice…</div>
        ) : detail ? (
          <div className="space-y-5 px-1 pb-1 pt-3 sm:px-2">
            <div className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
              <div className="flex flex-wrap items-start justify-between gap-4 pr-12">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-500">Purchase Invoice</p>
                  <p className="mt-1 text-xl font-semibold text-white">{detail.invoice_no || `PINV-${detail.id}`}</p>
                </div>
                <div className="text-right text-xs text-gray-400">
                  <p>Date: {detail.date || "—"}</p>
                  <p className="mt-1">
                    Tax:{" "}
                    <span className="text-gray-200">
                      {detail.tax_type === "GST"
                        ? `GST (${Number(detail.gst_rate || 0).toFixed(2)}%)`
                        : "Non-GST"}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                <p className="text-[11px] uppercase tracking-wider text-gray-500">Vendor</p>
                <p className="mt-2 text-sm text-gray-200">{detail.purchase_party?.name || "—"}</p>
                <p className="mt-1 text-xs text-gray-400">GST: {detail.purchase_party?.gst_number || "—"}</p>
                <p className="mt-1 text-xs text-gray-400">Contact: {detail.purchase_party?.contact_no || "—"}</p>
              </div>

              <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                <p className="text-[11px] uppercase tracking-wider text-gray-500">Reference</p>
                <p className="mt-2 text-sm text-gray-200">Financial Year: {detail.financial_year?.label || "—"}</p>
                <p className="mt-1 text-xs text-gray-400">Company: {detail.company?.name || "—"}</p>
                <p className="mt-1 text-xs text-gray-400">Challans: {(detail.challans || []).length}</p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-800 overflow-hidden">
              <div className="border-b border-gray-800 bg-gray-950/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Invoice Items
              </div>
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-950/60">
                    <th className="px-4 py-3 font-semibold text-gray-500">Challan</th>
                    <th className="px-4 py-3 font-semibold text-gray-500">Material</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-500">Qty</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-500">Rate (₹)</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-500">Amount (₹)</th>
                    <th className="px-4 py-3 font-semibold text-gray-500">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {detailItemRows.map((row) => (
                    <tr key={row.key} className="bg-gray-950/20">
                      <td className="px-4 py-3 text-gray-200">{row.challan_no}</td>
                      <td className="px-4 py-3 text-gray-200">{row.material_name}</td>
                      <td className="px-4 py-3 text-right text-gray-200">{fmt(row.qty, 3)}</td>
                      <td className="px-4 py-3 text-right text-gray-200">{fmt(row.rate, 2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-white">{fmt(row.amount, 2)}</td>
                      <td className="px-4 py-3 text-gray-400">{row.remark || "—"}</td>
                    </tr>
                  ))}
                  {detailItemRows.length === 0 && (
                    <tr className="bg-gray-950/20">
                      <td className="px-4 py-4 text-gray-500" colSpan={6}>
                        No items available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => printRef.current?.openPrint?.()}>
                  Print
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={deleteInvoice.isPending}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                  onClick={() => setInvoiceDeleteId(detail.id)}
                >
                  Delete
                </Button>
              </div>
              <div className="ml-auto w-full max-w-sm rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-300">
                    <span>Sub Total</span>
                    <span className="font-medium text-white">₹ {fmt(detail.sub_total, 2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>GST ({detail.tax_type === "GST" ? `${Number(detail.gst_rate || 0).toFixed(2)}%` : "—"})</span>
                    <span className="font-medium text-white">
                      ₹ {detail.tax_type === "GST" ? fmt(detail.gst_amount, 2) : "0.00"}
                    </span>
                  </div>
                  <div className="mt-2 border-t border-gray-800 pt-2 flex justify-between text-base">
                    <span className="font-semibold text-gray-200">Grand Total</span>
                    <span className="font-semibold text-white">₹ {fmt(detail.total_amount, 2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <PurchaseInvoicePrint ref={printRef} invoice={detail} />
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={invoiceDeleteId != null}
        onClose={() => {
          if (!deleteInvoice.isPending) setInvoiceDeleteId(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete purchase invoice?</h3>
          <p className="text-sm leading-relaxed text-gray-400">
            {invoicePendingDelete?.invoice_no ? (
              <>
                This will permanently remove invoice{" "}
                <span className="font-medium text-gray-200">
                  {invoicePendingDelete.invoice_no || `PINV-${invoicePendingDelete.id}`}
                </span>
                . Related challans may become available for invoicing again.
              </>
            ) : (
              <>This will permanently remove this invoice.</>
            )}
          </p>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={deleteInvoice.isPending}
              onClick={() => setInvoiceDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={deleteInvoice.isPending}
              className="bg-red-600 hover:bg-red-700"
              onClick={() => invoiceDeleteId != null && deleteInvoice.mutate(invoiceDeleteId)}
            >
              {deleteInvoice.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
